import type { Shape } from '@clicklinx/protocol'
import type { CSSProperties, ReactElement } from 'react'

/**
 * The eight shapes, drawn on a shared 100x100 grid so they read at the same
 * optical weight. They are separated by silhouette - vertex count, symmetry,
 * whether they have a hole - rather than by colour, so the game is playable
 * without colour vision. Nothing here is an emoji or an icon font: they are
 * paths, so they stay crisp at any size and inherit colour from the parent.
 */
const GEOMETRY: Record<Shape, ReactElement> = {
  circle: <circle cx="50" cy="50" r="43" />,
  triangle: <polygon points="50,6 94,88 6,88" />,
  square: <rect x="10" y="10" width="80" height="80" rx="9" />,
  diamond: <polygon points="50,4 96,50 50,96 4,50" />,
  hexagon: <polygon points="50,4 89.8,27 89.8,73 50,96 10.2,73 10.2,27" />,
  star: (
    <polygon points="50,4 61.2,34.6 93.8,35.8 68.1,55.9 77,87.2 50,69 23,87.2 31.9,55.9 6.3,35.8 38.8,34.6" />
  ),
  cross: (
    <polygon points="36,8 64,8 64,36 92,36 92,64 64,64 64,92 36,92 36,64 8,64 8,36 36,36" />
  ),
  // Drawn as a stroke rather than a filled path so the hole is unmistakable
  // even at the smallest sizes a dense late round produces.
  ring: <circle cx="50" cy="50" r="34" fill="none" stroke="currentColor" strokeWidth="17" />,
}

interface ShapeGlyphProps {
  shape: Shape
  /** Any CSS length. Defaults to filling its container. */
  size?: string | number
  className?: string
  style?: CSSProperties
  /** Decorative by default; pass a label when the shape carries meaning alone. */
  label?: string
}

export function ShapeGlyph({ shape, size = '100%', className, style, label }: ShapeGlyphProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      fill="currentColor"
      className={className}
      style={style}
      role={label ? 'img' : 'presentation'}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {GEOMETRY[shape]}
    </svg>
  )
}
